/**
 * A labelled form field.
 *
 * Almost all of this component is accessibility wiring, and almost all of it is
 * the part hand-rolled forms get wrong:
 *
 * - The label is a real `<label>` bound by `htmlFor`, so clicking it focuses
 *   the input and a screen reader announces the two together. A `<div>` styled
 *   as a label announces nothing.
 * - The error and the hint are referenced by `aria-describedby`, so they are
 *   read out with the field rather than sitting nearby unannounced.
 * - `aria-invalid` marks the field as failing, which is what assistive
 *   technology uses to say so — colour alone reaches nobody who cannot see it.
 * - The error carries `role="alert"`, so it is announced when it appears
 *   rather than only when the user happens to navigate onto the field.
 * - `required` is mirrored in `aria-required`, and the visual asterisk is
 *   hidden from screen readers because they already announce "required" —
 *   otherwise it reads as "star".
 */

import { useId, type ReactNode } from "react";

import "./forms.css";

export interface FieldProps {
  label: string;
  /** The input, wired up by the render prop below. */
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean;
    "aria-required": boolean;
  }) => ReactNode;
  error?: string | undefined;
  hint?: string | undefined;
  required?: boolean;
}

export function Field({ label, children, error, hint, required = false }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  // Both, when both exist. A field with a hint AND an error must announce the
  // error first — the user needs to know what went wrong before being told how
  // the field works.
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className="hardauto-field" data-invalid={error ? "true" : undefined}>
      <label className="hardauto-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="hardauto-field__required" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </label>

      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": Boolean(error),
        "aria-required": required,
      })}

      {hint ? (
        <p className="hardauto-field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}

      {error ? (
        <p className="hardauto-field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
